import React, { useState, useEffect } from 'react';
import { scheduleService } from '../services/scheduleService';
import { formatDate, formatTime } from '../utils/dateFormatter';
import LatestEpisodeCard from '../player/LatestEpisodeCard';
import { formatTime as formatPlaybackTime, parseItunesDuration } from '../player/format';
import Dismissible from './common/Dismissible';
import WaitlistBanner from './billing/WaitlistBanner';
import { usePlan } from '../hooks/usePlan';
import { openPricingModal } from '../utils/pricingModal';
import './home.css';

/**
 * Renewal/period-end date, formatted the same way as PricingPage's
 * formatPeriodEnd (duplicated here rather than imported — PricingPage is
 * off-limits to touch, and this is a one-line date format, not worth a
 * shared-util detour).
 */
function formatPeriodEnd(iso) {
  if (!iso) return null;
  try {
    return new Date(iso).toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' });
  } catch (error) {
    return null;
  }
}

/**
 * Compact "Your plan" row-card shown between the stat strip and the
 * onboarding checklist. Renders nothing while usePlan is still loading (no
 * flash), and nothing for a waitlisted free user — WaitlistBanner already
 * covers that state at the top of Home.
 */
function PlanCard() {
  const { plan, status, currentPeriodEnd, loading, resolved } = usePlan();

  if (loading || !resolved) return null; // never display a plan state the server hasn't confirmed
  if (plan === 'free' && status === 'waitlisted') return null;

  if (plan === 'free') {
    return (
      <div className="home-plan-card home-plan-card--upsell">
        <div className="home-plan-card__text">
          <span className="home-plan-card__title">Go Plus</span>
          <span className="home-plan-card__detail">$8.99/mo or $69/yr</span>
          <ul className="home-plan-card__benefits">
            <li>3 episodes/day</li>
            <li>60-min episodes</li>
            <li>your own news sources</li>
          </ul>
          {/* Copy Requirement (§ Topic 2 build plan): free plan generates 1 episode/day, and
              scheduled generation pauses after 7 days without a sign-in. */}
          <p className="home-plan-card__note">
            Free plan: 1 scheduled episode per day. To keep scheduled generation active, sign in
            at least once every 7 days — if you miss a week, your schedules pause until your next
            sign-in.
          </p>
        </div>
        <button
          type="button"
          className="btn btn-primary btn-sm home-plan-card__cta"
          onClick={openPricingModal}
        >
          Upgrade
        </button>
      </div>
    );
  }

  if (plan === 'plus') {
    const periodEndText = formatPeriodEnd(currentPeriodEnd);
    const detail = status === 'canceled_pending'
      ? (periodEndText ? `Plus until ${periodEndText}` : 'Cancels at period end')
      : (periodEndText ? `Renews ${periodEndText}` : 'Active');
    return (
      <div className="home-plan-card">
        <div className="home-plan-card__text">
          <span className="home-plan-card__title">Plus</span>
          <span className="home-plan-card__detail">{detail}</span>
        </div>
        <button
          type="button"
          className="btn btn-secondary btn-sm home-plan-card__cta"
          onClick={openPricingModal}
        >
          Manage subscription
        </button>
      </div>
    );
  }

  if (plan === 'comp') {
    return (
      <div className="home-plan-card">
        <div className="home-plan-card__text">
          <span className="home-plan-card__title">Comp account</span>
          <span className="home-plan-card__detail">Full access.</span>
        </div>
        <button
          type="button"
          className="plan-upgrade-link home-plan-card__link"
          onClick={openPricingModal}
        >
          View plans
        </button>
      </div>
    );
  }

  return null;
}

/** "Good morning/afternoon/evening" by local time. */
function getGreetingPrefix() {
  const hour = new Date().getHours();
  if (hour < 12) return 'Good morning';
  if (hour < 18) return 'Good afternoon';
  return 'Good evening';
}

/**
 * First name if the user object carries one (Cognito given_name/name
 * attributes). Email-only signups have neither — return null so the
 * greeting renders without a name (never "undefined").
 */
function getFirstName(user) {
  const name = user?.attributes?.given_name || user?.attributes?.name || '';
  const first = String(name).trim().split(/\s+/)[0];
  return first || null;
}

function Dashboard({ user, setActiveTab }) {
  const [stats, setStats] = useState({
    totalSources: 0,
    totalSchedules: 0,
    totalPodcasts: 0,
    lastGenerated: null
  });
  const [latestEpisode, setLatestEpisode] = useState(null);
  const [loading, setLoading] = useState(true);

  // Load user statistics
  useEffect(() => {
    const loadStats = async () => {
      try {
        setLoading(true);

        // Get news sources from localStorage
        const sources = JSON.parse(localStorage.getItem(`sources_${user.username}`) || '[]');

        // Get schedules from API
        const schedules = await scheduleService.listSchedules('active');

        // Count total podcasts and find latest from all RSS feeds
        let totalPodcastCount = 0;
        let latestPodcastDate = null;
        let newestEpisode = null; // full latest <item> across all feeds (T2)

        for (const schedule of schedules) {
          if (schedule.rssFeedUrl) {
            try {
              // Fetch RSS feed
              const rssResponse = await fetch(schedule.rssFeedUrl);
              const rssText = await rssResponse.text();

              // Parse RSS XML
              const parser = new DOMParser();
              const xmlDoc = parser.parseFromString(rssText, 'text/xml');
              const items = xmlDoc.getElementsByTagName('item');

              // Add to total count
              totalPodcastCount += items.length;

              // Check for latest date and retain the full latest item (T2)
              if (items.length > 0) {
                const latestItem = items[0]; // RSS feeds are newest first
                const pubDateElement = latestItem.getElementsByTagName('pubDate')[0];
                if (pubDateElement) {
                  const pubDate = new Date(pubDateElement.textContent);
                  if (!latestPodcastDate || pubDate > latestPodcastDate) {
                    latestPodcastDate = pubDate;

                    const guid = latestItem.getElementsByTagName('guid')[0]?.textContent;
                    const enclosure = latestItem.getElementsByTagName('enclosure')[0];
                    const durationSeconds = parseItunesDuration(
                      latestItem.getElementsByTagName('itunes:duration')[0]?.textContent
                    );

                    newestEpisode = {
                      id: guid || `${schedule.scheduleId}-latest`,
                      guid: guid || null,
                      title: latestItem.getElementsByTagName('title')[0]?.textContent || schedule.scheduleName,
                      audioUrl: enclosure?.getAttribute('url') || null,
                      pubDate: pubDate.toISOString(),
                      durationSeconds,
                      duration: durationSeconds != null ? formatPlaybackTime(durationSeconds) : 'N/A',
                      scheduleName: schedule.scheduleName,
                      sources: schedule.newsSources?.map(s => s.name) || [],
                      episodeFormat: schedule.episodeFormat || 'article'
                    };
                  }
                }
              }
            } catch (rssError) {
              console.warn(`⚠️ Could not fetch RSS feed for schedule ${schedule.scheduleId}:`, rssError);
            }
          }
        }

        setStats({
          totalSources: sources.length,
          totalSchedules: schedules.filter(s => s.status === 'active').length,
          totalPodcasts: totalPodcastCount,
          lastGenerated: latestPodcastDate ? latestPodcastDate.toISOString() : null
        });
        setLatestEpisode(newestEpisode);
      } catch (error) {
        console.error('❌ Error loading dashboard stats:', error);
        // Fallback to basic stats
        const sources = JSON.parse(localStorage.getItem(`sources_${user.username}`) || '[]');
        setStats({
          totalSources: sources.length,
          totalSchedules: 0,
          totalPodcasts: 0,
          lastGenerated: null
        });
        setLatestEpisode(null);
      } finally {
        setLoading(false);
      }
    };

    loadStats();
  }, [user.username]);

  const firstName = getFirstName(user);
  const greeting = getGreetingPrefix();

  // Onboarding checklist — live version of the old Quick Start Guide.
  const checklistSteps = [
    {
      id: 'sources',
      label: 'Add news sources',
      hint: 'Pick the websites your podcast reads from',
      done: stats.totalSources > 0,
      tab: 'sources'
    },
    {
      id: 'schedules',
      label: 'Create a schedule',
      hint: 'Choose language, voice and delivery time',
      done: stats.totalSchedules > 0,
      tab: 'schedules'
    },
    {
      id: 'episodes',
      label: 'Get your first episode',
      hint: 'Episodes generate automatically on your schedule',
      done: stats.totalPodcasts > 0,
      tab: 'podcasts'
    },
    {
      id: 'listen',
      label: 'Listen',
      hint: 'Play it here or in your podcast app via RSS',
      done: stats.totalPodcasts > 0,
      tab: 'podcasts'
    }
  ];
  const setupComplete = checklistSteps.every(step => step.done);

  // Empty-state CTA: no sources yet → start at Sources; otherwise Schedules.
  const emptyCtaTab = stats.totalSources === 0 ? 'sources' : 'schedules';
  const emptyCtaLabel = stats.totalSources === 0
    ? '📰 Add your first news source'
    : '📅 Create a schedule';

  return (
    <div className="component-card home">
      <WaitlistBanner />

      <h2 className="home-greeting">
        {firstName ? `${greeting}, ${firstName}` : greeting}
      </h2>

      {loading ? (
        <div className="home-loading">
          <p>⏳ Loading your podcasts...</p>
        </div>
      ) : (
        <>
          {latestEpisode ? (
            <LatestEpisodeCard episode={latestEpisode} />
          ) : (
            <div className="home-empty-hero">
              <div className="home-empty-hero__icon" aria-hidden="true">🎧</div>
              <p className="home-empty-hero__text">
                No episodes yet — your first podcast will appear here
              </p>
              <button
                type="button"
                className="btn btn-primary"
                onClick={() => setActiveTab && setActiveTab(emptyCtaTab)}
              >
                {emptyCtaLabel}
              </button>
            </div>
          )}

          <div className="home-stat-strip">
            <div className="home-stat">
              <span className="home-stat__label">📰 News Sources</span>
              <span className="home-stat__value">{stats.totalSources}</span>
              <span className="home-stat__caption">Active sources</span>
            </div>
            <div className="home-stat">
              <span className="home-stat__label">📅 Schedules</span>
              <span className="home-stat__value">{stats.totalSchedules}</span>
              <span className="home-stat__caption">Active schedules</span>
            </div>
            <div className="home-stat">
              <span className="home-stat__label">🎙️ Episodes</span>
              <span className="home-stat__value">{stats.totalPodcasts}</span>
              <span className="home-stat__caption">In your feeds</span>
            </div>
            <div className="home-stat">
              <span className="home-stat__label">⏰ Latest</span>
              {stats.lastGenerated ? (
                <>
                  <span className="home-stat__value home-stat__value--small">
                    {formatDate(stats.lastGenerated)}
                  </span>
                  <span className="home-stat__value home-stat__value--small">
                    {formatTime(stats.lastGenerated)}
                  </span>
                </>
              ) : (
                <span className="home-stat__value home-stat__value--small">Never</span>
              )}
              <span className="home-stat__caption">Recent podcast</span>
            </div>
          </div>

          <PlanCard />

          <Dismissible id="home-onboarding" title="Getting started" userSub={user?.username}>
            {setupComplete ? (
              <div className="home-setup-complete">Setup complete 🎉</div>
            ) : (
              <div className="home-checklist">
                <h3 className="home-checklist__title">🚀 Getting started</h3>
                <ol className="home-checklist__list">
                  {checklistSteps.map((step, index) => (
                    <li key={step.id}>
                      <button
                        type="button"
                        className={`home-checklist__step${step.done ? ' is-done' : ''}`}
                        onClick={() => setActiveTab && setActiveTab(step.tab)}
                      >
                        <span className="home-checklist__mark" aria-hidden="true">
                          {step.done ? '✓' : index + 1}
                        </span>
                        <span className="home-checklist__text">
                          <span className="home-checklist__label">{step.label}</span>
                          <span className="home-checklist__hint">{step.hint}</span>
                        </span>
                      </button>
                    </li>
                  ))}
                </ol>
              </div>
            )}
          </Dismissible>
        </>
      )}
    </div>
  );
}

export default Dashboard;
